import type { LocalizedText } from "./localizedText.js";
import type { PlannedExerciseEntry as PlannedExerciseEntryT } from "./schemas/plan.js";
import type { PlanTemplateEntry, ProgramTemplateSlot } from "./templateSchema.js";

// Compact-authoring helpers for the hand-written templates in
// templateData.ts. Split out of templates.ts (SP-TEMPLATES, 2026-07-27):
// keeping them here is what lets templateData.ts be PURE DATA, which is the
// condition for the data-file exemption to the 500-line cap
// (next/prompt.md §Limiti di dimensione file). Put no logic in the data file.
//
// Imports here are type-only, so this module has no runtime edge to the
// schemas; templateData does call these at module load, so the one runtime
// edge in the whole cluster is templateData -> templateAuthoring.

// ─── Helpers for compact data authoring ────────────────────────────────

export const entry = (
  exerciseId: string,
  exerciseName: string,
  setsArr: PlannedExerciseEntryT["sets"],
  notes?: LocalizedText,
): PlanTemplateEntry => ({
  exerciseId,
  exerciseName,
  sets: setsArr,
  ...(notes ? { notes } : {}),
});

// Repeat the same target N times. Beginner programmes spend a lot of
// time at "5×5 squat" — collapse that authoring noise.
export const repSets = (
  count: number,
  reps: number,
  weight:
    | { kind: "absolute"; weightKg: number }
    | { kind: "percentage"; pctOfMax: number }
    | { kind: "none" },
  rpe?: number,
): PlannedExerciseEntryT["sets"] =>
  Array.from({ length: count }, () => ({
    targetReps: reps,
    targetWeight: weight,
    ...(rpe !== undefined ? { targetRpe: rpe } : {}),
  }));

export const slot = (
  id: string,
  position: number,
  exerciseId: string,
  exerciseName: string,
  weeks: { sets: { reps: number; intensity: { kind: "none" } | { kind: "percent-of-max"; pct: number } | { kind: "absolute"; weightKg: number } | { kind: "rpe"; target: number } }[] }[],
  options?: { instructions?: LocalizedText; amrapLastSet?: boolean },
): ProgramTemplateSlot => ({
  id,
  position,
  variants: [
    { exerciseId, exerciseName, cadence: "every-week", phaseOffset: 0 },
  ],
  weeks,
  ...(options?.instructions ? { instructions: options.instructions } : {}),
  ...(options?.amrapLastSet ? { amrapLastSet: true } : {}),
});

// Generate the same week prescription N times. Stronglifts is the
// clearest case — every week is the same 5×5; the user adds load
// session-over-session, not week-over-week.
export const repeatWeek = (
  cycleWeeks: number,
  sets: { reps: number; intensity: { kind: "none" } | { kind: "percent-of-max"; pct: number } | { kind: "absolute"; weightKg: number } | { kind: "rpe"; target: number } }[],
): { sets: { reps: number; intensity: { kind: "none" } | { kind: "percent-of-max"; pct: number } | { kind: "absolute"; weightKg: number } | { kind: "rpe"; target: number } }[] }[] =>
  Array.from({ length: cycleWeeks }, () => ({ sets }));
